/**
 * SPA bootstrap (task 20.3).
 *
 * Startup order: load runtime config (window.__RUFUS_CONFIG__ or /config.json),
 * build the Cognito auth facade, show the login screen until authenticated,
 * then build the API client (with the JWT bearer) and mount the routed app.
 */

import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { loadConfig, type RufusRuntimeConfig } from "./config.js";
import { createCognitoAuth, type CognitoAuth } from "./auth.js";
import { ApiClient } from "./api-client.js";
import { App } from "./App.js";
import { LoginScreen } from "./screens/LoginScreen.js";
import {
  installTelemetry,
  captureConsoleErrors,
  installClickBreadcrumbs,
  type TelemetryController,
} from "./telemetry.js";

/**
 * How close to expiry (ms) the id token must be before a request refreshes it
 * proactively. 60s comfortably covers a slow turn round-trip plus minor clock
 * skew, so the token stays valid through the request without refreshing on
 * every call. Reactive 401 handling remains the backstop if this window misses.
 */
const PROACTIVE_REFRESH_WINDOW_MS = 60_000;

function Root({
  config,
  auth,
  telemetry,
}: {
  readonly config: RufusRuntimeConfig;
  readonly auth: CognitoAuth;
  readonly telemetry: TelemetryController;
}) {
  // handleRedirectCallback() ran before render, so token state is settled at
  // mount. Subscribe to auth changes so a SIGN-OUT — explicit, or a failed
  // refresh that clears the tokens (CognitoAuth.doRefresh) — re-renders this
  // root back to the LoginScreen instead of leaving a dead App holding a null
  // token (every call would 403). Without the subscription `authed` was captured
  // once and never updated, so a cleared session never returned to login.
  const [authed, setAuthed] = useState(auth.isAuthenticated());
  useEffect(() => {
    const off = auth.onChange(() => setAuthed(auth.isAuthenticated()));
    // Reconcile in case auth changed between render and effect attach.
    setAuthed(auth.isAuthenticated());
    return off;
  }, [auth]);

  if (!authed) {
    return <LoginScreen auth={auth} socialProviders={config.socialProviders} />;
  }

  const api = new ApiClient({
    baseUrl: config.apiUrl,
    getToken: () => auth.idToken,
    // Feed each request's METHOD /path → status into the telemetry breadcrumb
    // trail (context for a later crash). Breadcrumb only — a failed response is
    // never itself a reported error.
    breadcrumbs: telemetry,
    // PROACTIVE refresh: before each request, if the id token is within ~60s of
    // expiry, renew it first — so a turn submitted near expiry carries a fresh
    // token and the server never 401s (the measured mid-play stall). Swallows a
    // refresh failure (the reactive onUnauthorized below is the backstop).
    ensureFreshToken: async () => {
      if (auth.isExpiringWithin(PROACTIVE_REFRESH_WINDOW_MS)) {
        await auth.refresh();
      }
    },
    // On a 401, transparently refresh the id token (Hosted UI refresh-token
    // grant) and retry once. If refresh fails, tokens are cleared and the 401 is
    // surfaced; the user re-authenticates via the Hosted UI on the next action.
    onUnauthorized: () => auth.refresh(),
  });

  return <App services={{ config, api, auth }} />;
}

async function bootstrap() {
  const container = document.getElementById("root");
  if (!container) throw new Error("Missing #root element.");
  const root = createRoot(container);

  let config: RufusRuntimeConfig;
  let auth: CognitoAuth;
  try {
    config = await loadConfig();
    auth = createCognitoAuth(config);
  } catch (err) {
    // NOTE: config failed to load, so we can't install telemetry (no apiUrl).
    // The fatal-startup branch below surfaces the problem directly.
    // A genuinely fatal startup problem (no/invalid runtime config) — the SPA
    // cannot function at all, so surface it.
    root.render(
      <p role="alert">
        Rufus could not start: {err instanceof Error ? err.message : "unknown error"}
      </p>,
    );
    return;
  }

  // Install client error telemetry as early as we have an apiUrl, so a crash in
  // the auth callback or first render is captured. Best-effort: installTelemetry
  // never throws into the app, and console capture only REPORTS real Error
  // objects (a plain console.error string is a breadcrumb, not an issue).
  const telemetry = installTelemetry({
    apiBaseUrl: config.apiUrl,
    // The telemetry route is authenticated — supply the current id token so the
    // report carries a bearer. Reports fired while signed out are simply not
    // delivered (the server would reject them); in-session crashes carry a
    // verified identity. getAnchor defaults to the module-level anchor the play
    // view publishes (setTelemetryAnchor), so a crash points at the live
    // campaign/turn without threading a handle through the tree.
    getToken: () => auth.idToken,
    ...(config.buildInfo ? { clientBuild: config.buildInfo } : {}),
  });
  captureConsoleErrors(telemetry);
  // Record coarse click breadcrumbs (what the user pressed) so a crash report
  // carries the sequence of actions leading up to it. Secret-free selectors
  // only; breadcrumbs are never reported on their own.
  installClickBreadcrumbs(telemetry);

  // If we're returning from the Hosted UI with ?code=, exchange it for tokens
  // before first render so an authenticated user lands straight in the app. A
  // FAILURE here (stale/replayed code, state mismatch, transient token-endpoint
  // error) must NOT dead-end the app — it should fall back to the login screen
  // so the user can simply sign in again, not a fatal error page.
  try {
    await auth.handleRedirectCallback();
  } catch (err) {
    console.warn(
      "Sign-in callback failed; returning to login.",
      err instanceof Error ? err.message : err,
    );
    // handleRedirectCallback already stripped the transient state on a
    // state-mismatch; clear any lingering ?code so a reload doesn't retry a
    // spent code, then fall through to render the (unauthenticated) login.
    window.history.replaceState(null, "", window.location.origin);
  }

  // BOOTSTRAP PROACTIVE REFRESH (session-hardening fix 2): a page load may hold a
  // stored session whose ID TOKEN is already expired but whose REFRESH token is
  // still good (readStoredTokens keeps such a recoverable session). Renew it
  // BEFORE first render so the app never renders authenticated with a dead id
  // token (the zombie that 403s) — and never needlessly forces a re-login when the
  // session is recoverable. If the refresh fails (dead refresh token) the auth
  // layer clears the tokens, so Root then renders the login screen. Best-effort:
  // refresh() never throws.
  if (auth.isAuthenticated() && auth.isExpiringWithin(PROACTIVE_REFRESH_WINDOW_MS)) {
    await auth.refresh();
  }

  root.render(
    <StrictMode>
      <Root config={config} auth={auth} telemetry={telemetry} />
    </StrictMode>,
  );
}

void bootstrap();
